import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, RefreshCw, Store, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const REQUIRED_SCOPES = [
  "read_products",
  "unauthenticated_read_product_listings",
  "unauthenticated_read_product_inventory",
  "unauthenticated_read_product_tags",
  "unauthenticated_read_checkouts",
  "unauthenticated_write_checkouts",
  "unauthenticated_read_content",
];
const OPTIONAL_SCOPES = ["write_draft_orders"];

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right text-ink" data-testid={testId}>{value}</dd>
    </div>
  );
}

function ScopeChips({ scopes, granted, testId }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testId}>
      {scopes.map((s) => {
        const ok = granted.includes(s);
        return (
          <span key={s} className={`inline-flex items-center gap-1 px-2 py-1 font-mono text-[10px] ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`} data-testid={`scope-${s}`}>
            {ok ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />} {s}
          </span>
        );
      })}
    </div>
  );
}

function StatusBadge({ s }) {
  const ready = s?.connected && s?.ready;
  const label = ready ? "Connected — live catalogue" : s?.connected ? "Installed — scopes missing" : s?.configured ? "Configured — not connected" : "Not configured";
  return (
    <div className={`mt-5 inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-widest ${ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="shopify-status-badge">
      {ready ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {label}
    </div>
  );
}

function FixInstructions({ s }) {
  const storeHandle = s?.shop?.url?.match(/https?:\/\/([^.]+)\./)?.[1];
  return (
    <div className="mt-4 border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="shopify-error">
      <p className="font-medium">{s.error}</p>
      <ol className="mt-3 list-decimal space-y-1.5 pl-4 text-xs leading-relaxed text-amber-800">
        <li>Shopify Dev Dashboard → your app → <strong>Configuration / Access scopes</strong> → add every scope marked amber below and save (if the app uses <code>shopify.app.toml</code>, add them to <code>[access_scopes] scopes</code> and run <code>shopify app deploy</code>).</li>
        <li>Re-approve the app on the store so the new permissions apply{storeHandle ? <> — open <span className="font-mono break-all">https://admin.shopify.com/store/{storeHandle}/oauth/install?client_id=&lt;your client id&gt;</span></> : ""}.</li>
        <li>Press <strong>Reconnect &amp; refresh</strong> here.</li>
      </ol>
    </div>
  );
}

function ConnectionDetails({ data }) {
  const s = data?.shopify;
  const ready = s?.connected && s?.ready;
  return (
    <>
      <StatusBadge s={s} />
      <dl className="mt-4 space-y-2">
        <Row label="Catalogue source" value={data?.catalogue_source === "shopify" ? "Live Shopify catalogue" : "Sample catalogue (fallback)"} testId="shopify-catalogue-source" />
        {s?.shop && <Row label="Store" testId="shopify-shop-name" value={<ShopLink shop={s.shop} />} />}
        {ready && <Row label="Checkout mode" value={s.mode === "storefront" ? "Storefront cart → Shopify checkout" : "Draft order → Shopify checkout (fallback)"} testId="shopify-mode" />}
      </dl>
      {s?.connected && <Scopes granted={s.granted_scopes || []} />}
      {s?.error && <FixInstructions s={s} />}
      {ready && s?.storefront_error && (
        <p className="mt-3 text-xs text-muted-foreground">Storefront token unavailable — using Admin API fallback. Add the unauthenticated_* scopes and re-approve to enable cart-based checkout.</p>
      )}
    </>
  );
}

function ShopLink({ shop }) {
  return (
    <a href={shop.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-gold">
      {shop.name}{shop.currency ? ` · ${shop.currency}` : ""}{shop.product_count != null ? ` · ${shop.product_count} products` : ""} <ExternalLink size={12} />
    </a>
  );
}

function Scopes({ granted }) {
  return (
    <div className="mt-4">
      <p className="text-luxe text-[10px] text-ink">Access scopes</p>
      <ScopeChips scopes={REQUIRED_SCOPES} granted={granted} testId="shopify-required-scopes" />
      <p className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">Optional (fallback checkout)</p>
      <ScopeChips scopes={OPTIONAL_SCOPES} granted={granted} testId="shopify-optional-scopes" />
    </div>
  );
}

export function ShopifyConnectionCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["shop-status"], queryFn: async () => (await api.get("/shop/status")).data });
  const refresh = useMutation({
    mutationFn: async () => (await api.post("/shop/refresh")).data,
    onSuccess: (res) => {
      ["shop-status", "products", "product", "collection", "collections", "facets"].forEach((key) => qc.invalidateQueries({ queryKey: [key] }));
      toast.success(res.catalogue_source === "shopify" ? `Live catalogue loaded — ${res.product_count} products from Shopify` : "Shopify catalogue not readable yet — showing the sample catalogue");
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail) || "Refresh failed"),
  });

  return (
    <div className="border border-border bg-white/60 p-6 sm:col-span-2" data-testid="shopify-connection-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Store size={20} className="text-gold" />
          <div>
            <h3 className="text-luxe text-[11px] text-ink">Shopify Store Connection</h3>
            <p className="mt-1 text-xs text-muted-foreground">Products, collections and checkout are served live from your store once connected.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          data-testid="shopify-refresh-button"
          className="inline-flex items-center gap-2 border border-ink px-4 py-2.5 text-[11px] uppercase tracking-[0.16em] text-ink transition-colors hover:bg-ink hover:text-ivory disabled:opacity-50"
        >
          <RefreshCw size={13} className={refresh.isPending ? "animate-spin" : ""} /> {refresh.isPending ? "Checking…" : "Reconnect & refresh"}
        </button>
      </div>

      {isLoading ? <p className="mt-5 text-sm text-muted-foreground">Checking connection…</p> : <ConnectionDetails data={data} />}
    </div>
  );
}
